import { useState, useCallback, useEffect } from 'react';
import { pipelineService } from '../../../../services/pipelineService';
import { dispatchCustomEvent, listener } from '../../../../utils/dispatch';

export default function usePipelineDrafts({ companyId }) {
  const [drafts, setDrafts] = useState([]);
  const [totalDrafts, setTotalDrafts] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [loadingList, setLoadingList] = useState(false);
  const [pendingReclassify, setPendingReclassify] = useState(null);

  const [reclassifying, setReclassifying] = useState(false);

  const fetchDrafts = useCallback(async (pageNum = page) => {
    setLoadingList(true);
    try {
      const res = await pipelineService.getDrafts(pageNum, limit, 'pending', companyId);
      const draftsList = res.data?.data || res.data || [];
      setDrafts(draftsList);
      setTotalDrafts(res.data?.total || res.total || draftsList.length);
    } catch (e) {
      console.error("Error loading pending drafts", e);
      dispatchCustomEvent('popup', { message: 'Error al cargar documentos del Auxiliar Contable.', type: 'error' });
    } finally {
      setLoadingList(false);
    }
  }, [page, limit, companyId]);

  const handleDiscardDraft = (id) => {
    dispatchCustomEvent('notification-confirm-discard-draft', id);
  };

  const performDiscardDraft = async (idOrObj) => {
    const id = (typeof idOrObj === 'object' && idOrObj !== null) ? (idOrObj.id || idOrObj.data?.id || idOrObj.data) : idOrObj;
    if (!id) return;
    try {
      await pipelineService.deleteDraft(id, companyId);
      dispatchCustomEvent('popup', { message: 'Borrador descartado correctamente.', type: 'success' });
      fetchDrafts();
    } catch (e) {
      dispatchCustomEvent('popup', { message: 'Error al descartar el borrador.', type: 'error' });
    }
  };

  const handleReclassifyFromTable = async (draftId, newType) => {
    setReclassifying(true);
    try {
      const res = await pipelineService.reprocessDraft(draftId, newType, companyId);
      const updatedDraft = res.data?.data || res.data;
      const label = newType === 'buy' ? 'Compra' : newType === 'bank_statement' ? 'Extracto Bancario' : 'Gasto';
      dispatchCustomEvent('popup', {
        message: `Documento re-clasificado como "${label}" y reprocesado con IA.`,
        type: 'success'
      });
      await fetchDrafts(page);
      return updatedDraft;
    } catch (err) {
      console.error('Error reclasificando borrador', err);
      const errMsg = err.response?.data?.message || 'Error al reprocesar el documento con IA.';
      dispatchCustomEvent('popup', { message: errMsg, type: 'error' });
      return null;
    } finally {
      setReclassifying(false);
    }
  };


  const stageReclassify = (draftId, newType) => {
    const label = newType === 'buy' ? 'Compra' : newType === 'bank_statement' ? 'Extracto Bancario' : 'Gasto';
    setPendingReclassify({ draftId, newType, label });
  };

  const confirmReclassify = async () => {
    if (!pendingReclassify) return null;
    const { draftId, newType } = pendingReclassify;
    setPendingReclassify(null);
    return await handleReclassifyFromTable(draftId, newType);
  };


  const cancelReclassify = () => setPendingReclassify(null);

  useEffect(() => {
    fetchDrafts(page);

    const cleanDiscard = listener('discard-pipeline-draft', (e) => {
      const target = e.detail;
      if (target) {
        performDiscardDraft(target);
      }
    });

    const cleanReload = listener('reload-pipeline-drafts', () => {
      fetchDrafts(page);
    });

    return () => {
      cleanDiscard();
      cleanReload();
    };
  }, [companyId, fetchDrafts, page]);

  return {
    drafts, totalDrafts, page, limit, loadingList, pendingReclassify, reclassifying,
    setPage, fetchDrafts, handleDiscardDraft, performDiscardDraft,
    stageReclassify, confirmReclassify, cancelReclassify, handleReclassifyFromTable
  };
}

